import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { usePersonIdentification } from "../contexts/PersonIdentificationContext";
import { useDemoMode } from "../contexts/DemoModeContext";
import { apiFetch } from "../lib/api";
import { AlertTriangle } from "lucide-react";

const tabs = [
  { label: "Home", path: "/concierge" },
  { label: "Cameras", path: "/" },
  { label: "Roster", path: "/roster" },
];

type Level2Resident = { id: number; name: string; room: string; last_seen: string | null };

export default function NavigationBar() {
  const { totalQueueCount, showPopup } = usePersonIdentification();
  const { demoMode, toggleDemoMode, displayName } = useDemoMode();
  const [level2, setLevel2] = useState<Level2Resident[]>([]);
  const [level2Open, setLevel2Open] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiFetch("/safety_level2");
        const data = await res.json();
        if (!cancelled) setLevel2(data.residents || []);
      } catch (e) {
        console.error("Failed to load Level II list:", e);
      }
    };
    load();                                   // immediately on mount
    const id = setInterval(load, 60_000);     // then every 60s
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Live updates over SSE
  useEffect(() => {
    const es = new EventSource("https://api.monivue.io/events");
    es.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "safety_level2_alert") {
          setLevel2(msg.residents || []);
        }
      } catch {
        /* ignore non-JSON keepalives */
      }
    };
    es.onerror = () => { /* EventSource auto-reconnects */ };
    return () => es.close();
  }, []);

  return (
    <div className="relative flex items-center bg-gray-800 p-3">
      {/* Left: Tabs */}
      <div className="flex items-center">
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            className={({ isActive }) =>
              `px-4 py-2 rounded text-white mr-2 ${
                isActive ? "bg-blue-600" : "hover:bg-gray-700"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </div>

      {/* Center: Title */}
      <div
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none
             mix-blend-difference text-white/80 font-semibold tracking-wide
             text-base md:text-lg"
        aria-hidden="true"
      >
        monivue
      </div>

      {/* Right */}
      <div className="ml-auto flex items-center gap-2">
        {/* Level II running list */}
        {level2.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setLevel2Open((o) => !o)}
              className="flex items-center gap-2 bg-rose-700 hover:bg-rose-600 text-white font-semibold px-3 py-2 rounded-lg"
            >
              <AlertTriangle className="w-4 h-4" />
              Level II: {level2.length}
            </button>
            {level2Open && (
              <div className="absolute right-0 mt-2 w-64 rounded-lg bg-[#0d1117] border border-rose-700/40 shadow-2xl z-50 max-h-80 overflow-y-auto">
                <div className="px-3 py-2 text-xs font-semibold text-rose-300 border-b border-white/10">
                  Not seen in 2+ hours
                </div>
                <ul className="py-1">
                  {level2.map((r) => (
                    <li key={r.id} className="px-3 py-2 text-sm text-rose-100 hover:bg-white/5">
                      <div>{displayName(r.id, r.name)}</div>
                      <div className="text-xs text-gray-400">
                        {r.room ? `Room ${r.room} · ` : ""}
                        {r.last_seen
                          ? `last seen ${new Date(r.last_seen).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                          : "never seen today"}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          role="switch"
          aria-checked={demoMode}
          onClick={toggleDemoMode}
          title="Demo mode masks resident names for screen-sharing. Display only — no data changes."
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            demoMode
              ? "bg-purple-600 text-white hover:bg-purple-500"
              : "bg-gray-700 text-gray-200 hover:bg-gray-600"
          }`}
        >
          <span
            className={`inline-block w-8 h-4 rounded-full relative transition-colors ${
              demoMode ? "bg-purple-300" : "bg-gray-500"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                demoMode ? "translate-x-4" : ""
              }`}
            />
          </span>
          Demo
        </button>

        {totalQueueCount > 0 && (
          <button
            onClick={showPopup}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-2 rounded-lg animate-pulse"
          >
            <UserPlus className="w-4 h-4" />
            {totalQueueCount}
          </button>
        )}

        <button
          className="px-4 py-2 text-white bg-sky-600 hover:bg-sky-500"
          onClick={() => window.open("/threshold-view", "_blank", "width=980,height=720")}
        >
          View Threshold
        </button>

        <button
          className="px-4 py-2 text-white bg-amber-500 hover:bg-amber-600"
          onClick={() => window.open("/threshold-config", "_blank", "width=980,height=720")}
        >
          Configure Threshold
        </button>
      </div>
    </div>
  );
}